import React from 'react';
import { motion } from 'framer-motion';
import { LayoutDashboard, History, Star, Key, Zap, ArrowRight, ExternalLink, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const UserDashboard: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-7xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Interface
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <div>
            <div className="flex items-center gap-3 mb-6">
              <LayoutDashboard className="w-8 h-8 text-emerald-500" />
              <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-white uppercase">User Dashboard</h1>
            </div>
            <p className="text-[#525252] font-mono text-lg leading-relaxed">
              Manage your saved traces, API keys, and active fork sessions.
            </p>
          </div>
          <div className="flex gap-4">
            <div className="p-4 rounded-2xl bg-[#0F0F0F] border border-[#262626] flex flex-col gap-1">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#404040]">Active Forks</span>
              <span className="text-xl font-bold text-white">0</span>
            </div>
            <div className="p-4 rounded-2xl bg-[#0F0F0F] border border-[#262626] flex flex-col gap-1">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#404040]">Saved Traces</span>
              <span className="text-xl font-bold text-white">12</span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-8">
            <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-8 flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Activity
              </h3>
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-[#1A1A1A] border border-[#262626] group hover:border-emerald-500/30 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded bg-emerald-500/10 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-emerald-500" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-mono text-white">0x1234...5678</span>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#525252]">Ethereum Mainnet • 2 hours ago</span>
                      </div>
                    </div>
                    <button className="p-2 hover:bg-white/5 rounded transition-colors text-[#525252] group-hover:text-white">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button className="w-full mt-6 py-3 text-[10px] font-bold uppercase tracking-widest text-[#525252] hover:text-white transition-colors border-t border-[#262626]">
                View All Activity
              </button>
            </section>

            <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-8 flex items-center gap-2">
                <Key className="w-4 h-4" />
                API Keys
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-[#1A1A1A] border border-[#262626]">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold text-white uppercase tracking-tight">Default Key</span>
                    <span className="text-[10px] font-mono text-[#525252]">Created on March 1, 2026</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-[#404040]">oru_live_********************</span>
                    <button className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Copy</button>
                  </div>
                </div>
              </div>
              <button className="mt-6 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-500 hover:text-emerald-400 transition-colors">
                Create New API Key
                <ArrowRight className="w-3 h-3" />
              </button>
            </section>
          </div>

          <div className="space-y-8">
            <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-6 flex items-center gap-2">
                <Star className="w-4 h-4" />
                Saved Traces
              </h3>
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[#1A1A1A] border border-[#262626] flex flex-col gap-1 hover:border-emerald-500/30 transition-all cursor-pointer">
                    <span className="text-[10px] font-bold text-white uppercase tracking-tight">Euler Exploit Analysis</span>
                    <span className="text-[9px] font-mono text-[#525252]">0xabcd...ef01</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="p-8 rounded-3xl bg-emerald-500 text-black">
              <h3 className="text-xl font-bold mb-4 uppercase tracking-tighter leading-none">Pro Plan</h3>
              <p className="text-black/70 font-mono text-xs leading-relaxed mb-6">
                Unlock unlimited fork sessions, advanced gas analytics, and team collaboration tools.
              </p>
              <button className="w-full bg-black text-white font-bold py-3 rounded-lg text-[10px] uppercase tracking-widest hover:bg-black/80 transition-all">
                Upgrade Now
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
